import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";
import { setupGlobalErrorCapture } from "./lib/clientEnv";
import { initNativeBootstrap } from "./lib/nativeBootstrap";
import { initTokenStorage } from "./lib/nativeTokenStorage";
import { replayMetaConsentOnBoot } from "./lib/metaConsent";
import { captureWebAttributionFromLocation } from "./lib/webAttribution";

// Iter XX.285 — Capture Meta / UTM / fbclid attribution on boot.
// Runs BEFORE React renders so a signup that happens on the SAME
// page load still sees the payload. Latest-touch semantics; a
// param-less visit does NOT erase existing storage.
try { captureWebAttributionFromLocation(); } catch { /* fail-soft */ }

// Iter XX.295 — Expose the baked-in frontend build id for diagnostics.
// The purchase-diagnostics endpoint reads `window.__GULLY_APP_BUILD__`
// (previously never set, so build attribution was always null).
try {
  window.__GULLY_APP_BUILD__ = process.env.REACT_APP_BUILD_ID || null;
} catch { /* fail-soft */ }

// Iter 42.4 — Global JS-error / unhandled-rejection / page-hide capture so
// we can detect when Instagram WebView kills the tab mid-upload. Fire-
// and-forget; never blocks the page. Reads the active session_id from
// localStorage at post time.
setupGlobalErrorCapture({
  getSessionId: () => {
    try { return localStorage.getItem("gully_web_session_id"); } catch { return null; }
  },
  apiBase: (process.env.REACT_APP_BACKEND_URL || "") + "/api",
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));
// Iter XX.177 — Native Token Storage Hardening.
// We MUST await `initTokenStorage()` before React renders on native,
// so the very first paint (which triggers the first `/api/*` call via
// the axios interceptor) already has the JWT hydrated from the OS
// secure store. On web this resolves synchronously (no-op) so there's
// no user-visible delay. Native hydration is a single Keychain /
// EncryptedSharedPreferences read — sub-millisecond in practice.
(async () => {
  try { await initTokenStorage(); } catch { /* fail closed — unauthenticated */ }
  // Native shell setup (splash hide, status bar tint, keyboard events,
  // offline banner). No-op on web. Fire-and-forget so we don't block
  // the initial render on plugin lifecycle work.
  initNativeBootstrap();
  // Iter XX.200 — Meta App Events consent replay. If the user previously
  // granted, re-initialise the Facebook Android SDK on this cold-start
  // so App Launch fires within seconds. No-op on web / iOS and no-op
  // when consent is denied or unset. Fire-and-forget with a structured
  // console breadcrumb for chrome://inspect / logcat forensics — audit
  // row also written server-side for founder-side observability.
  replayMetaConsentOnBoot().then((r) => {
    try {
      console.info("[metaConsent] boot replay result",
        { replayed: !!(r && r.replayed), reason: (r && r.reason) || "unknown" });
    } catch { /* noop */ }
  }).catch(() => {});
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </React.StrictMode>,
  );
})();
